// 📁 src/components/account/PersonalSettings.jsx

import React, { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

export default function PersonalSettings() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoMessage, setLogoMessage] = useState('');
  const [showPasswordCard, setShowPasswordCard] = useState(false);
  const [showContactCard, setShowContactCard] = useState(false);
  const [showLogoCard, setShowLogoCard] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [tenantId, setTenantId] = useState('');

  useEffect(() => {
    const email = localStorage.getItem('email');
    const registry = JSON.parse(localStorage.getItem('accountRegistry') || '{}');
    const account = registry[email];
    if (account) {
      setCompanyName(account.companyName || '');
      setTenantId(account.tenantId || '');
    }
  }, []);

  const handlePasswordChange = (e) => {
    e.preventDefault();
    const email = localStorage.getItem('email');
    const registry = JSON.parse(localStorage.getItem('accountRegistry') || '{}');
    if (!registry[email]) {
      setPasswordMessage('❌ Account not found.');
      return;
    }
    if (currentPassword !== registry[email]?.password) {
      setPasswordMessage('❌ Current password is incorrect.');
      return;
    }
    if (!newPassword || !confirmPassword) {
      setPasswordMessage('❌ Please fill in all fields.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordMessage('❌ New passwords do not match.');
      return;
    }
    registry[email].password = newPassword;
    localStorage.setItem('accountRegistry', JSON.stringify(registry));
    console.log('✅ Password updated in localStorage');
    setPasswordMessage('✅ Password changed successfully.');
  };

  const handleContactUpdate = (e) => {
    e.preventDefault();
    if (!contactNumber) {
      setContactMessage('❌ Please enter a contact number.');
      return;
    }
    sessionStorage.setItem('contactNumber', contactNumber);
    console.log('✅ Contact number updated');
    setContactMessage('✅ Contact number updated successfully.');
  };

  const handleLogoUpload = (e) => {
    e.preventDefault();
    if (!logoFile) {
      setLogoMessage('❌ Please select a logo file.');
      return;
    }

    const email = localStorage.getItem('email');
    const registry = JSON.parse(localStorage.getItem('accountRegistry') || '{}');

    if (!registry[email]) {
      setLogoMessage('❌ Account not found.');
      return;
    }

    registry[email].companyLogo = logoFile.name;
    localStorage.setItem('accountRegistry', JSON.stringify(registry));
    sessionStorage.setItem('companyLogo', logoFile.name);

    console.log('✅ Logo uploaded:', logoFile.name);
    setLogoMessage(`✅ Logo "${logoFile.name}" uploaded successfully.`);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <h2 className="text-2xl font-bold text-indigo-700 mb-2">Account Preferences</h2>
      {(companyName || tenantId) && (
        <div className="mb-6 text-sm text-gray-600 italic">
          {companyName && <div>Company Name: {companyName}</div>}
          {tenantId && <div>Tenant ID: {tenantId}</div>}
        </div>
      )}
      <Outlet />

      {/* 🔐 Change Password Card */}
      <div className="mb-6 border rounded shadow bg-white">
        <button
          onClick={() => setShowPasswordCard(!showPasswordCard)}
          className="w-full text-left px-6 py-4 font-semibold text-indigo-700 hover:bg-indigo-50 transition flex justify-between items-center"
        >
          <span>Change Password</span>
          <span>{showPasswordCard ? '▾' : '▸'}</span>
        </button>
        {showPasswordCard && (
          <form onSubmit={handlePasswordChange} className="px-6 pb-6 space-y-4">
            <div className="relative">
              <input
                type={showCurrent ? 'text' : 'password'}
                placeholder="Current Password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                className="w-full p-3 border rounded pr-10"
              />
              <span
                className="absolute top-1/2 right-3 transform -translate-y-1/2 text-gray-500 cursor-pointer"
                onClick={() => setShowCurrent(!showCurrent)}
              >
                {showCurrent ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>

            <div className="relative">
              <input
                type={showNew ? 'text' : 'password'}
                placeholder="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                className="w-full p-3 border rounded pr-10"
              />
              <span
                className="absolute top-1/2 right-3 transform -translate-y-1/2 text-gray-500 cursor-pointer"
                onClick={() => setShowNew(!showNew)}
              >
                {showNew ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>

            <div className="relative">
              <input
                type={showConfirm ? 'text' : 'password'}
                placeholder="Confirm New Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="w-full p-3 border rounded pr-10"
              />
              <span
                className="absolute top-1/2 right-3 transform -translate-y-1/2 text-gray-500 cursor-pointer"
                onClick={() => setShowConfirm(!showConfirm)}
              >
                {showConfirm ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>

            <button
              type="submit"
              className="w-full bg-indigo-600 text-white py-3 rounded hover:bg-indigo-700"
            >
              Update Password
            </button>
            {passwordMessage && (
              <p className="text-sm text-center text-green-600">{passwordMessage}</p>
            )}
          </form>
        )}
      </div>

      {/* 📞 Update Contact Number Card */}
      <div className="mb-6 border rounded shadow bg-white">
        <button
          onClick={() => setShowContactCard(!showContactCard)}
          className="w-full text-left px-6 py-4 font-semibold text-indigo-700 hover:bg-indigo-50 transition flex justify-between items-center"
        >
          <span>Update Contact Number</span>
          <span>{showContactCard ? '▾' : '▸'}</span>
        </button>
        {showContactCard && (
          <form onSubmit={handleContactUpdate} className="px-6 pb-6 space-y-4">
            <input
              type="text"
              placeholder="Enter new contact number"
              value={contactNumber}
              onChange={(e) => setContactNumber(e.target.value)}
              required
              className="w-full p-3 border rounded"
            />
            <button
              type="submit"
              className="w-full bg-indigo-600 text-white py-3 rounded hover:bg-indigo-700"
            >
              Update Contact
            </button>
            {contactMessage && (
              <p className="text-sm text-center text-green-600">{contactMessage}</p>
            )}
          </form>
        )}
      </div>

      {/* 🏢 Upload Company Logo Card */}
      <div className="mb-6 border rounded shadow bg-white">
        <button
          onClick={() => setShowLogoCard(!showLogoCard)}
          className="w-full text-left px-6 py-4 font-semibold text-indigo-700 hover:bg-indigo-50 transition flex justify-between items-center"
        >
          <span>Upload Company Logo</span>
          <span>{showLogoCard ? '▾' : '▸'}</span>
        </button>
        {showLogoCard && (
          <form onSubmit={handleLogoUpload} className="px-6 pb-6 space-y-4">
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setLogoFile(e.target.files[0])}
              className="w-full p-3 border rounded"
            />
            <button
              type="submit"
              className="w-full bg-indigo-600 text-white py-3 rounded hover:bg-indigo-700"
            >
              Upload Logo
            </button>
            {logoMessage && <p className="text-sm text-center text-green-600">{logoMessage}</p>}
          </form>
        )}
      </div>
    </div>
  );
}
